"use client"

import { useMemo, useState } from "react"
import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ui/shad"
import { cn } from "@ui/utils"
import { Check, User as UserIcon, Users, X } from "lucide-react"

import { useFetchTeams } from "@/hooks/api/teams"
import { useFetchUsers } from "@/hooks/api/users"

interface CcUserTeamPickerProps {
  userIds: string[]
  teamIds: string[]
  onUserIdsChange: (userIds: string[]) => void
  onTeamIdsChange: (teamIds: string[]) => void
}

export function CcUserTeamPicker({ userIds, teamIds, onUserIdsChange, onTeamIdsChange }: CcUserTeamPickerProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  const { data: usersData } = useFetchUsers()
  const { data: teamsData } = useFetchTeams()

  const users = useMemo(() => usersData?.users ?? [], [usersData?.users])
  const teams = useMemo(() => teamsData?.teams ?? [], [teamsData?.teams])

  const filteredUsers = useMemo(() => {
    const query = searchQuery.toLowerCase()
    const filtered = query
      ? users.filter((u) => u.full_name?.toLowerCase().includes(query) || u.email?.toLowerCase().includes(query))
      : users
    return filtered.slice(0, 10)
  }, [users, searchQuery])

  const filteredTeams = useMemo(() => {
    const query = searchQuery.toLowerCase()
    const filtered = query ? teams.filter((t) => t.name?.toLowerCase().includes(query)) : teams
    return filtered.slice(0, 10)
  }, [teams, searchQuery])

  const toggleUser = (userId: string) => {
    onUserIdsChange(userIds.includes(userId) ? userIds.filter((id) => id !== userId) : [...userIds, userId])
  }

  const toggleTeam = (teamId: string) => {
    onTeamIdsChange(teamIds.includes(teamId) ? teamIds.filter((id) => id !== teamId) : [...teamIds, teamId])
  }

  const hasSelection = userIds.length > 0 || teamIds.length > 0
  const hasResults = filteredUsers.length > 0 || filteredTeams.length > 0

  const selectedUsers = useMemo(() => users.filter((u) => userIds.includes(u.id)), [users, userIds])
  const selectedTeams = useMemo(() => teams.filter((t) => teamIds.includes(t.id)), [teams, teamIds])

  return (
    <div className="space-y-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "w-full justify-between gap-2 px-3 text-sm font-normal",
              !hasSelection && "text-muted-foreground"
            )}
          >
            {hasSelection ? `${userIds.length + teamIds.length} selected` : "Select users or teams..."}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-64 p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Search users or teams..." value={searchQuery} onValueChange={setSearchQuery} />
            <CommandList>
              {searchQuery && !hasResults && (
                <CommandEmpty className="text-muted-foreground py-3 text-center text-sm">No results found</CommandEmpty>
              )}

              {filteredUsers.length > 0 && (
                <CommandGroup heading="Users">
                  {filteredUsers.map((user) => (
                    <CommandItem
                      key={user.id}
                      value={`user-${user.id}`}
                      onSelect={() => toggleUser(user.id)}
                      className="flex items-center gap-2"
                    >
                      <UserIcon className="text-muted-foreground size-4" />
                      <span className="flex-1 truncate">{user.full_name}</span>
                      {userIds.includes(user.id) && <Check className="text-foreground size-4" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {filteredTeams.length > 0 && (
                <CommandGroup heading="Teams">
                  {filteredTeams.map((team) => (
                    <CommandItem
                      key={team.id}
                      value={`team-${team.id}`}
                      onSelect={() => toggleTeam(team.id)}
                      className="flex items-center gap-2"
                    >
                      <div className="flex items-center gap-1.5">
                        {team.color && (
                          <div className="size-2.5 rounded-full" style={{ backgroundColor: team.color }} />
                        )}
                        <Users className="text-muted-foreground size-4" />
                      </div>
                      <span className="flex-1 truncate">{team.name}</span>
                      {teamIds.includes(team.id) && <Check className="text-foreground size-4" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {hasSelection && (
        <div className="flex flex-wrap gap-1.5">
          {selectedUsers.map((user) => (
            <Badge key={user.id} variant="secondary" className="gap-1 pr-1">
              <UserIcon className="size-3" />
              {user.full_name}
              <button
                type="button"
                onClick={() => onUserIdsChange(userIds.filter((id) => id !== user.id))}
                className="hover:bg-muted ml-0.5 rounded-sm p-0.5"
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
          {selectedTeams.map((team) => (
            <Badge key={team.id} variant="secondary" className="gap-1 pr-1">
              {team.color && <div className="size-2 rounded-full" style={{ backgroundColor: team.color }} />}
              <Users className="size-3" />
              {team.name}
              <button
                type="button"
                onClick={() => onTeamIdsChange(teamIds.filter((id) => id !== team.id))}
                className="hover:bg-muted ml-0.5 rounded-sm p-0.5"
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}
